import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("spc_token") || sessionStorage.getItem("spc_token"));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!!(localStorage.getItem("spc_token") || sessionStorage.getItem("spc_token")));

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/me");
      setUser(data);
      return data;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    refreshUser().finally(() => setLoading(false));
  }, [token, refreshUser]);

  const login = useCallback((tok, userObj) => {
    localStorage.setItem("spc_token", tok);
    localStorage.setItem("spc_email", userObj?.email || "");
    // Clean up any legacy session-scoped tokens from the previous build
    sessionStorage.removeItem("spc_token");
    sessionStorage.removeItem("spc_email");
    setToken(tok);
    setUser(userObj);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("spc_token");
    localStorage.removeItem("spc_email");
    sessionStorage.removeItem("spc_token");
    sessionStorage.removeItem("spc_email");
    localStorage.removeItem("spc_active_plan");
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      token, user, email: user?.email || localStorage.getItem("spc_email") || sessionStorage.getItem("spc_email"),
      isAuthed: !!token, hasAccess: !!user?.hasAccess, hasTracker: !!user?.hasTracker,
      planTier: user?.planTier || null,
      racePack: user?.racePack || null,
      distanceUnit: user?.distanceUnit || "mi",
      weightUnit: user?.weightUnit || "kg",
      planHydrationBaseline: user?.planHydrationBaseline ?? null,
      planGoalWeight: user?.planGoalWeight ?? null,
      isTester: !!user?.isTester, testTier: user?.testTier || null,
      subscription: user?.subscription || null,
      trackerTrialUsed: !!user?.trackerTrialUsed, isAdmin: user?.role === "admin",
      isCoach: user?.role === "coach" || user?.role === "admin",
      role: user?.role || "member",
      coachEmail: user?.coachEmail || null,
      avatarPath: user?.avatarPath || null,
      weightTracking: user?.weightTracking || "weekly",
      // Opt-out default — enabled unless the user explicitly turned it off.
      progressPhotosEnabled: user?.progressPhotosEnabled !== false,
      progressPhotosPrompted: !!user?.progressPhotosPrompted,
      loading, login, logout, refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
